/**
 * @fileoverview Origen de un enlace de factura (`/facturas/:token`) y el copy que depende de el.
 * El token no dice de donde vino por si solo: lo informa el backend (`ValidarEnlace` y `OrigenEnlace`).
 * @module origenEnlace
 */

/** Origenes que el backend puede informar para un enlace de factura. */
export const ORIGENES_ENLACE = ['PowerBI', 'Correo', 'Desconocido'] as const;

/**
 * @typedef OrigenEnlace
 * @description De donde viene el enlace: un reporte de Power BI, el acuse por correo, o no se sabe
 * (token invalido, formato viejo o backend anterior que no informa el origen).
 */
export type OrigenEnlace = (typeof ORIGENES_ENLACE)[number];

/**
 * @function normalizarOrigenEnlace
 * @description Convierte un valor externo (respuesta del backend o sessionStorage) en un origen valido.
 * Nunca se confia en una cadena arbitraria: cualquier cosa fuera de los tres valores es `Desconocido`.
 * @param {unknown} valor - Valor a validar.
 * @returns {OrigenEnlace} El mismo origen si es uno de los permitidos; si no, `Desconocido`.
 */
export function normalizarOrigenEnlace(valor: unknown): OrigenEnlace {
  return ORIGENES_ENLACE.find((origen) => origen === valor) ?? 'Desconocido';
}

/**
 * @typedef TextosOrigenEnlace
 * @description Copy que cambia segun de donde viene el enlace.
 */
export type TextosOrigenEnlace = {
  /** Aviso del login cuando hay una factura pendiente de abrir. */
  avisoLogin: string;
  /** Descripcion de la pantalla "Enlace no valido". */
  enlaceInvalido: string;
  /** Subtitulo de la pantalla de carga mientras se valida el enlace. */
  validando: string;
};

/** Copy por origen, en un solo lugar para ajustar el texto sin tocar la logica. */
export const TEXTOS_ORIGEN_ENLACE: Record<OrigenEnlace, TextosOrigenEnlace> = {
  PowerBI: {
    avisoLogin:
      'Inicia sesión para abrir la factura que seleccionaste en Power BI.',
    enlaceInvalido:
      'No pudimos abrir esta factura con este enlace. Vuelve a abrirla desde Power BI.',
    validando: 'Validando el enlace de Power BI…',
  },
  Correo: {
    avisoLogin:
      'Inicia sesión para abrir la factura del correo que recibiste.',
    enlaceInvalido:
      'No pudimos abrir esta factura con este enlace. Vuelve a abrirla desde el correo.',
    validando: 'Validando el enlace del correo…',
  },
  Desconocido: {
    avisoLogin: 'Inicia sesión para abrir la factura del enlace.',
    enlaceInvalido:
      'No pudimos abrir esta factura con este enlace. Vuelve a abrirla desde donde la recibiste.',
    validando: 'Validando el enlace…',
  },
};
